import logger from '../utils/logger';
import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Heart, ArrowRight, Loader2, Download } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DonationSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [donation, setDonation] = useState(null);
  const [donationId, setDonationId] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkDonationStatus(sessionId);
    } else {
      setStatus('success');
    }
  }, [searchParams]);

  const checkDonationStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/donations/status/${sessionId}`);
      setDonation(response.data);
      setStatus(response.data.payment_status === 'paid' ? 'success' : 'pending');
      // Fetch donation ID from session
      try {
        const donRes = await axios.get(`${BACKEND_URL}/api/donations/by-session/${sessionId}`);
        if (donRes.data?.id) setDonationId(donRes.data.id);
      } catch (e) { logger.error('Error fetching donation ID:', e); }
    } catch (error) {
      logger.error('Error checking status:', error);
      setStatus('success');
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#BA1826]" />
      </div>
    );
  }

  return (
    <div>
      {/* Red Header Bar */}
      <div className="bg-[#BA1826] py-6 text-center">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">Thank You</h1>
      </div>

      <div className="py-16 px-6">
        <div className="max-w-lg mx-auto text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>

          <h2 className="font-heading text-3xl font-bold text-[#0A0A0A] mb-4">
            Your Donation Was Successful!
          </h2>

          {donation && (
            <div className="mb-6">
              <p className="text-lg text-[#4B5563] mb-2">
                Amount: <span className="font-semibold text-[#BA1826]">${donation.amount}</span>
              </p>
              {donation.campaign?.name && (
                <div className="bg-white border border-[#E5E7EB] rounded-xl px-6 py-4 mb-4 inline-block text-left"
                     data-testid="campaign-designation">
                  <p className="text-xs uppercase tracking-wide text-[#6B7280] mb-1">You supported</p>
                  <p className="font-heading text-lg font-bold text-[#0A0A0A]">
                    {donation.campaign.name}
                  </p>
                  {donation.campaign.country_name && (
                    <p className="text-sm text-[#4B5563] mt-0.5">{donation.campaign.country_name}</p>
                  )}
                </div>
              )}
              {/* Impact reinforcement */}
              <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-xl px-6 py-4 inline-block" data-testid="impact-reinforcement">
                <p className="font-heading text-lg font-bold text-[#BA1826]">
                  {donation.amount >= 100
                    ? `You just supported ${Math.floor(donation.amount / 100)} ${Math.floor(donation.amount / 100) === 1 ? 'family' : 'families'}.`
                    : donation.amount >= 50
                    ? 'You just supported a family for 2 weeks.'
                    : 'Your support makes a real difference.'}
                </p>
              </div>
            </div>
          )}

          <p className="text-[#4B5563] mb-8 leading-relaxed">
            Thank you for your generous contribution to the Novena Humanitarian Foundation. 
            Your support helps us provide essential aid to families and communities in need 
            with dignity and compassion.
          </p>

          <div className="flex items-center justify-center gap-2 text-[#BA1826] mb-8">
            <Heart className="w-5 h-5" />
            <span className="font-medium">You've lit a candle of hope</span>
          </div>

          {donationId && (
            <div className="mb-6">
              <a
                href={donation?.receipt_url || `${BACKEND_URL}/api/public/receipt/${donationId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#F8F9FA] border border-[#E5E7EB] text-[#0A0A0A] px-5 py-2.5 rounded text-sm font-medium hover:bg-[#F1F5F9] transition-colors"
                data-testid="download-receipt-btn"
              >
                <Download className="w-4 h-4 text-[#BA1826]" />
                Download Donation Receipt (PDF)
              </a>
              <p className="text-xs text-[#4B5563] mt-2">
                Please retain this receipt for your records.
              </p>
            </div>
          )}

          <div className="space-y-4">
            <Link to="/" className="btn-primary inline-flex">
              Return to Homepage
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-sm text-[#4B5563]">
              Have questions?{' '}
              <Link to="/contact" className="text-[#BA1826] hover:underline">
                Contact us
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Quote */}
      <div className="py-8 bg-[#F8F9FA] border-t border-[#E5E7EB]">
        <div className="text-center">
          <p className="font-heading text-xl italic text-[#4B5563]">
            "Transparency without exploitation. Support without shame."
          </p>
        </div>
      </div>
    </div>
  );
};

export default DonationSuccess;
